import { makeAutoObservable, observable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type { TIssueActivity } from "@/types/issue";

export class IssueActivityStore {
  activityByIssueId = observable.map<string, TIssueActivity[]>();
  loadingByIssueId = observable.map<string, boolean>();
  errorByIssueId = observable.map<string, string | null>();
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  getActivity(issueId: string): TIssueActivity[] {
    return this.activityByIssueId.get(issueId) ?? [];
  }

  /** Revalidate the feed after a mutation, but only when it has been fetched
   *  before — an unopened feed fetches fresh on first open anyway. */
  async refreshIfCached(workspaceSlug: string, projectId: string, issueId: string): Promise<void> {
    if (!this.activityByIssueId.has(issueId)) return;
    await this.fetchActivity(workspaceSlug, projectId, issueId);
  }

  async fetchActivity(workspaceSlug: string, projectId: string, issueId: string): Promise<void> {
    this.loadingByIssueId.set(issueId, true);
    this.errorByIssueId.set(issueId, null);
    try {
      const activity = await this.rootStore.workItemService.getWorkItemActivity(
        workspaceSlug,
        projectId,
        issueId,
      );
      runInAction(() => {
        this.activityByIssueId.set(issueId, activity);
        this.loadingByIssueId.set(issueId, false);
      });
    } catch (error) {
      console.error("Failed to fetch work-item activity", {
        workspaceSlug,
        projectId,
        issueId,
        error,
      });
      runInAction(() => {
        this.loadingByIssueId.set(issueId, false);
        this.errorByIssueId.set(issueId, "Failed to fetch activity");
      });
    }
  }

  /** Workspace-scoped fetch for work items addressed without a project (events).
   *  Shares the same per-issue cache, so the panel reads it via `getActivity`. */
  async fetchWorkspaceActivity(workspaceSlug: string, issueId: string): Promise<void> {
    this.loadingByIssueId.set(issueId, true);
    this.errorByIssueId.set(issueId, null);
    try {
      const activity = await this.rootStore.workItemService.getWorkspaceWorkItemActivity(
        workspaceSlug,
        issueId,
      );
      runInAction(() => {
        this.activityByIssueId.set(issueId, activity);
        this.loadingByIssueId.set(issueId, false);
      });
    } catch (error) {
      console.error("Failed to fetch workspace work-item activity", {
        workspaceSlug,
        issueId,
        error,
      });
      runInAction(() => {
        this.loadingByIssueId.set(issueId, false);
        this.errorByIssueId.set(issueId, "Failed to fetch activity");
      });
    }
  }
}
